import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plane, Hotel, Car, Ship, TrainFront, Bus, UtensilsCrossed, Tag, Stamp, Shield,
  ClipboardCheck, MapPinned, Users, Image as ImageIcon, LayoutGrid, Mic, Search,
  ChevronRight, Newspaper, MessageSquareWarning, ArrowRight, LifeBuoy,
} from "lucide-react";
import api from "../lib/api";
import SEOHead from "../components/SEOHead";
import BrandContract from "../components/BrandContract";
import { websiteLd, organizationLd } from "../lib/structuredData";
import LocalPanel from "../components/LocalPanel";
import PopularAircraft from "../components/PopularAircraft";
import TrendingPlaces from "../components/TrendingPlaces";
import { StatBox, Pill, fmtNum, Spinner } from "../components/common";
import { GoogleFlightsCard } from "../components/GoogleFlightsCard";
import { HomeStoriesRow } from "../components/home/StoriesRow";
import { GetAppCard } from "../components/home/GetAppCard";
import HomeFeatureCards from "../components/home/HomeFeatureCards";
import HomeCommunityFeed from "../components/home/HomeCommunityFeed";
import HomeAdventureCTA from "../components/home/HomeAdventureCTA";
import HomeFeaturedDestinations from "../components/home/HomeFeaturedDestinations";
import MspTopBanner from "../components/home/MspTopBanner";
import OpenAppHero from "../components/home/OpenAppHero";
import CityGuideRow from "../components/home/CityGuideRow";
import AviationPulseStrip from "../components/home/AviationPulseStrip";
import OnboardingStrip from "../components/home/OnboardingStrip";
import PulseNetworkFeed from "../components/network/PulseNetworkFeed";
import InstallQrCard from "../components/InstallQrCard";
import PushToggle from "../components/PushToggle";
import BrandMark from "../components/BrandMark";
import { SITE } from "../lib/site";

/* My Social Pulse — Home (owner mockup, Feb 2026).
   Mobile-first, native-app layout: hero photo with editorial italic
   serif title, pill search, 5x3 pastel icon grid, My Trips + My
   Documents cards, upcoming-trip carousel. Desktop reflows into a
   two-column layout with the sections below the fold intact. */

const ICONS_GRID = [
  { icon: Plane,          label: "Flights",    to: "/airlines",              tint: "bg-sky-100 text-sky-600" },
  { icon: Hotel,          label: "Hotels",     to: "/stays",                 tint: "bg-fuchsia-100 text-fuchsia-600" },
  { icon: Car,            label: "Cars",       to: "/rideshare",             tint: "bg-emerald-100 text-emerald-600" },
  { icon: Ship,           label: "Cruises",    to: "/water",                 tint: "bg-blue-100 text-blue-600" },
  { icon: TrainFront,     label: "Trains",     to: "/rail",                  tint: "bg-orange-100 text-orange-500" },
  { icon: Bus,            label: "Buses",      to: "/bus",                   tint: "bg-indigo-100 text-indigo-600" },
  { icon: UtensilsCrossed,label: "Dining",     to: "/marketplace",           tint: "bg-red-100 text-red-500" },
  { icon: Tag,            label: "Deals",      to: "/loyalty",               tint: "bg-amber-100 text-amber-600" },
  { icon: Stamp,          label: "Visas",      to: "/visas",                 tint: "bg-violet-100 text-violet-600" },
  { icon: Shield,         label: "Insurance",  to: "/features#insurance",    tint: "bg-teal-100 text-teal-600" },
  { icon: ClipboardCheck, label: "Checklists", to: "/packing",               tint: "bg-cyan-100 text-cyan-600" },
  { icon: MapPinned,      label: "Maps",       to: "/map",                   tint: "bg-lime-100 text-lime-600" },
  { icon: Users,          label: "Travelers",  to: "/community",             tint: "bg-rose-100 text-rose-500" },
  { icon: ImageIcon,      label: "Photos",     to: "/community",             tint: "bg-pink-100 text-pink-500" },
  { icon: LayoutGrid,     label: "More",       to: "/features",              tint: "bg-zinc-100 text-zinc-600" },
];

const UPCOMING_TRIPS = [
  {
    id: "tokyo-27",
    title: "Tokyo Adventure",
    dates: "Apr 12 – Apr 25, 2027",
    days: 12,
    travelers: 4,
    photo: "https://images.unsplash.com/photo-1542051841857-5f90071e7989?auto=format&fit=crop&w=800&q=70",
    to: "/travel-hub",
  },
  {
    id: "paris-26",
    title: "Paris Getaway",
    dates: "May 20 – May 27, 2026",
    days: 7,
    travelers: 2,
    photo: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=800&q=70",
    to: "/travel-hub",
  },
  {
    id: "kerala-26",
    title: "Kerala Backwaters",
    dates: "Nov 3 – Nov 12, 2026",
    days: 9,
    travelers: 3,
    photo: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&w=800&q=70",
    to: "/travel-hub",
  },
];

export default function Home() {
  const [stats, setStats] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/complaints", { params: { sort: "top", limit: 4 } }).then((r) => setComplaints(r.data.items)).catch(() => {});
    api.get("/articles", { params: { limit: 4 } }).then((r) => setArticles(r.data.items)).catch(() => {});
  }, []);

  return (
    <div className="overflow-x-hidden bg-background" data-testid="home-page">
      <SEOHead
        title={`${SITE.name} — Your Trip. Your Journey. Your Memories.`}
        description={`${SITE.name} is the personal travel operating system — plan the trip, manage the journey, capture the experience and preserve the memories. Part of the SocialPulse network.`}
        canonical="/"
        type="website"
        jsonLd={[websiteLd(), organizationLd()]}
      />
      {/* HOME — Design-1 layout: hero → feature cards → destinations → community → CTA */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8 space-y-10">
        <OpenAppHero />
        <BrandContract />
        <MspTopBanner />
        <CityGuideRow city={{ name: "Portland", region: "Oregon", country: "USA" }} />
        <AviationPulseStrip />
        {/* Compact cross-portal feed — auto-hides at 0 articles */}
        <PulseNetworkFeed portalSlug="travelerpulse" limit={8} variant="strip" />
        {/* Laptop → phone install shortcut */}
        <InstallQrCard variant="compact" className="hidden md:flex" />
        <OnboardingStrip />
        <NativePushPrompt />
        <HomeFeatureCards />
        <HomeFeaturedDestinations />
        <HomeCommunityFeed limit={3} />
        <HomeAdventureCTA />

        {/* Signed-in shortcuts — kept below the editorial layout for members */}
        <details className="rounded-2xl border border-border bg-card p-4 group" data-testid="home-quick-actions">
          <summary className="cursor-pointer list-none flex items-center justify-between font-serif font-700 text-lg text-primary">
            <span>Quick actions</span>
            <span className="text-xs font-mono uppercase tracking-widest text-accent group-open:hidden">Show</span>
            <span className="text-xs font-mono uppercase tracking-widest text-accent hidden group-open:inline">Hide</span>
          </summary>
          <div className="mt-4 md:grid md:grid-cols-[minmax(0,1fr)_360px] md:gap-6 space-y-4 md:space-y-0">
            <div className="space-y-4 min-w-0">
              <SearchChips />
              <IconGrid />
              <ShortcutRow />
            </div>
            <div className="md:sticky md:top-24 md:self-start">
              <UpcomingTripCarousel />
            </div>
          </div>
        </details>
      </div>

      {/* WEB-ONLY DEEPER SECTIONS (desktop / logged-in web experience) */}
      <div className="max-w-6xl mx-auto px-5 lg:px-8 space-y-10">
        <section className="mt-4"><GoogleFlightsCard compact /></section>
        <section><GetAppCard /></section>
        <section><TrendingPlaces limit={6} /></section>
        <HomeStoriesRow />

        {/* STATS */}
        <section className="mt-4 relative z-10" data-testid="home-stats">
          {stats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border rounded-2xl overflow-hidden">
              <StatBox value={fmtNum(stats.airports)} label="Airports" testid="stat-airports" />
              <StatBox value={fmtNum(stats.airlines)} label="Airlines" testid="stat-airlines" />
              <StatBox value={fmtNum(stats.routes)} label="Routes" testid="stat-routes" />
              <StatBox value={fmtNum(stats.countries)} label="Countries" testid="stat-countries" />
            </div>
          ) : <div className="h-28" />}
        </section>

        <LocalPanel />

        {/* Flight recovery banner */}
        <section className="mt-8">
          <Link to="/recovery" data-testid="home-recovery-banner"
            className="flex flex-wrap items-center gap-3 border border-border bg-card rounded-2xl px-5 py-4 hover:border-accent hover:shadow-warm-md transition-[border-color,box-shadow] tap">
            <LifeBuoy className="w-6 h-6 text-destructive shrink-0" strokeWidth={1.75} />
            <span className="text-base">
              <b className="text-primary">Flight delayed or cancelled?</b>{" "}
              <span className="text-muted-foreground">Explore your options with the Flight Recovery Assistant.</span>
            </span>
            <span className="ml-auto text-base font-700 text-accent">Get help →</span>
          </Link>
        </section>

        {/* COMPLAINTS + NEWS */}
        <section className="py-14 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif font-700 text-3xl tracking-tight flex items-center gap-2 text-primary">
                <MessageSquareWarning className="w-6 h-6 text-destructive" strokeWidth={1.75} /> Top complaints
              </h2>
              <Link to="/complaints" className="text-sm text-accent hover:underline font-700">View all</Link>
            </div>
            <div className="space-y-3">
              {complaints.length === 0 ? <Spinner /> : complaints.map((c) => (
                <Link key={c.complaint_id} to={`/complaints/${c.complaint_id}`} data-testid={`home-complaint-${c.complaint_id}`}
                  className="block border border-border bg-card rounded-2xl p-5 hover:-translate-y-px hover:border-accent transition-[transform,border-color] tap">
                  <div className="flex items-center gap-2 mb-2">
                    <Pill tone="red">{c.category}</Pill>
                    <Pill>{c.entity_name}</Pill>
                    {c.official_response && <Pill tone="green">Official response</Pill>}
                  </div>
                  <h3 className="font-serif font-700 text-lg leading-snug">{c.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.body}</p>
                  <div className="mt-2 text-xs font-mono text-muted-foreground">▲ {c.upvote_count} upvotes · {c.comment_count} comments</div>
                </Link>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif font-700 text-2xl tracking-tight flex items-center gap-2 text-primary">
                <Newspaper className="w-6 h-6 text-accent" strokeWidth={1.75} /> News
              </h2>
              <Link to="/news" className="text-sm text-accent hover:underline font-700">More</Link>
            </div>
            <div className="space-y-3">
              {articles.map((a) => (
                <Link key={a.slug} to={`/news/${a.slug}`} className="block border border-border bg-card rounded-2xl p-4 hover:border-accent transition-colors">
                  <h3 className="font-serif font-700 text-base leading-snug">{a.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.summary}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <PopularAircraft />

        {/* ABOUT */}
        <section className="py-12" data-testid="home-about-caption">
          <div className="rounded-2xl bg-card border border-border p-10 sm:p-14 text-center shadow-warm-sm">
            <BrandMark size="lg" subline testid="home-about-brand" />
            <p className="font-serif italic font-700 text-2xl sm:text-3xl tracking-tight max-w-3xl mx-auto text-primary leading-tight mt-4">
              Your complete travel life — planned, stored, organized and shared, all in one warm, private place.
            </p>
            <p className="text-base text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed">
              Trips, documents, passports, reminders, expenses and a private AI travel assistant — for you and your family. Made simple enough for anyone.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/about" data-testid="home-about-link"
                className="px-6 py-3 rounded-full bg-primary text-primary-foreground text-base font-700 hover:opacity-95 transition-opacity min-h-[48px] inline-flex items-center tap">
                Learn more →
              </Link>
              <Link to="/about/founder" data-testid="home-founder-link"
                className="px-6 py-3 rounded-full border-2 border-border text-base font-700 text-foreground hover:border-primary hover:text-primary transition-colors min-h-[48px] inline-flex items-center tap">
                Meet the Founder
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ============= HERO POSTER — Design-1 style "Explore the World, Together" ============= */

// Rotate the hero backdrop across a curated set so returning visitors don't
// see the same shot on every load. Kept small so browsers eagerly cache them.
const HERO_IMAGES = [
  { src: "https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?auto=format&fit=crop&w=1600&q=75", alt: "Santorini overlooking the Aegean sea" },
  { src: "https://images.unsplash.com/photo-1545569341-9eb8b30979d9?auto=format&fit=crop&w=1600&q=75", alt: "Fushimi Inari torii gates, Kyoto" },
  { src: "https://images.unsplash.com/photo-1553603227-2358aabe821e?auto=format&fit=crop&w=1600&q=75", alt: "Marrakech medina rooftops at dusk" },
  { src: "https://images.unsplash.com/photo-1531177071211-cd1af5936e7d?auto=format&fit=crop&w=1600&q=75", alt: "Torres del Paine peaks, Patagonia" },
  { src: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=1600&q=75", alt: "Eiffel Tower and Paris skyline" },
  { src: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?auto=format&fit=crop&w=1600&q=75", alt: "Colosseum at golden hour, Rome" },
];

const HERO_CHIPS = [
  { label: "Hotels",      to: "/stays" },
  { label: "Flights",     to: "/airlines" },
  { label: "Restaurants", to: "/marketplace" },
  { label: "Things to Do", to: "/explore-places" },
];

const HeroCard = () => {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [hero] = useState(() => HERO_IMAGES[Math.floor(Math.random() * HERO_IMAGES.length)]);
  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    nav(`/search?q=${encodeURIComponent(term)}`);
  };
  return (
    <section className="relative rounded-3xl overflow-hidden shadow-warm-md border border-border" data-testid="home-hero-card">
      <img src={hero.src} alt={hero.alt} loading="eager"
        className="absolute inset-0 w-full h-full object-cover" />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-white/10" />
      <div className="relative p-6 sm:p-10 lg:p-12 max-w-2xl">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-accent mb-3 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="home-hero-eyebrow">
          <span>Plan</span><span className="text-primary/40">·</span>
          <span>Share</span><span className="text-primary/40">·</span>
          <span>Explore</span><span className="text-primary/40">·</span>
          <span>Belong</span>
        </div>
        <h1 className="font-serif font-800 leading-[1.05] tracking-tight text-primary text-4xl sm:text-5xl lg:text-6xl" data-testid="home-hero-title">
          Explore the World,<br />Together.
        </h1>
        <p className="mt-4 text-base sm:text-lg text-foreground/80 max-w-lg leading-relaxed" data-testid="home-hero-subtitle">
          Discover amazing places, get trusted travel tips, plan your trips, and connect with a global community of travellers.
        </p>
        <form onSubmit={submit} data-testid="home-hero-search"
          className="mt-6 flex items-center bg-white rounded-full shadow-warm-md border border-border pl-4 pr-1 py-1 max-w-lg">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="home-hero-search-input"
            placeholder="Search destinations, hotels, experiences..."
            className="flex-1 min-w-0 bg-transparent outline-none px-3 py-2 text-sm text-primary placeholder:text-muted-foreground" />
          <button type="submit" data-testid="home-hero-search-btn"
            className="shrink-0 inline-flex items-center gap-1 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-700 hover:opacity-95 min-h-[36px]">
            Search
          </button>
        </form>
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="home-hero-chips">
          {HERO_CHIPS.map(({ label, to }) => (
            <Link key={label} to={to} data-testid={`home-hero-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/95 border border-border text-sm font-700 text-primary hover:border-accent hover:text-accent transition-colors min-h-[36px]">
              {label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============= SEARCH CHIPS ============= */

/* Native App Preview: when `?native=1` is active we surface the push toggle
   on the home screen so first-timers grant permission before hitting a
   nudge screen. Hidden once permission is granted or on non-native visits. */
const NativePushPrompt = () => {
  let native = false;
  try { native = sessionStorage.getItem("tp_native_preview") === "1"; } catch { native = false; }
  if (!native) return null;
  try {
    if (typeof Notification !== "undefined" && Notification.permission === "granted") return null;
  } catch { /* silent */ }
  return (
    <section data-testid="home-native-push-prompt" className="rounded-2xl">
      <PushToggle />
    </section>
  );
};

const CHIPS = [
  { label: "Hotels",       to: "/stays" },
  { label: "Flights",      to: "/airlines" },
  { label: "Restaurants",  to: "/marketplace" },
  { label: "Things to Do", to: "/explore-places" },
];

const SearchChips = () => (
  <section className="flex flex-wrap items-center gap-2" data-testid="home-search-chips">
    {CHIPS.map(({ label, to }) => (
      <Link key={label} to={to} data-testid={`home-search-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-card border border-border text-sm font-700 text-primary hover:border-accent hover:text-accent transition-colors tap min-h-[36px]">
        {label}
      </Link>
    ))}
  </section>
);

/* ============= PILL SEARCH ============= */

const SearchPill = () => {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const v = q.trim();
    if (v) nav(`/explore-places?q=${encodeURIComponent(v)}`);
  };
  const mic = () => {
    try {
      const R = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!R) return; // silently no-op if the browser doesn't support voice
      const r = new R();
      r.lang = "en-US"; r.interimResults = false; r.maxAlternatives = 1;
      r.onresult = (ev) => setQ(ev.results[0][0].transcript || "");
      r.start();
    } catch { /* silent */ }
  };
  return (
    <form onSubmit={submit} data-testid="home-search-pill"
      className="flex items-center gap-2 bg-card border border-border rounded-full shadow-warm-md pl-4 pr-2 py-1.5 min-h-[56px]">
      <Search className="w-5 h-5 text-primary/60 shrink-0" strokeWidth={1.75} />
      <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="home-search-input"
        placeholder="Search destinations, hotels, flights, experiences…"
        className="flex-1 min-w-0 bg-transparent outline-none text-base text-primary placeholder:text-muted-foreground" />
      <button type="button" onClick={mic} aria-label="Voice search" data-testid="home-search-mic"
        className="shrink-0 w-10 h-10 rounded-full bg-accent/10 text-accent hover:bg-accent/20 flex items-center justify-center tap">
        <Mic className="w-4 h-4" strokeWidth={1.75} />
      </button>
    </form>
  );
};

/* ============= 5×3 PASTEL ICON GRID ============= */

const IconGrid = () => (
  <section className="grid grid-cols-5 gap-2.5 sm:gap-3" data-testid="home-icon-grid">
    {ICONS_GRID.map(({ icon: Icon, label, to, tint }) => (
      <Link key={label} to={to} data-testid={`home-icon-${label.toLowerCase()}`}
        className={`group flex flex-col items-center justify-center gap-1.5 rounded-2xl ${tint.split(" ")[0]} border border-transparent px-1.5 py-3 min-h-[80px] shadow-warm-sm hover:shadow-warm-md hover:-translate-y-0.5 transition-[transform,box-shadow] tap`}>
        <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${tint.split(" ")[1]}`} strokeWidth={2} />
        <span className="text-[11px] sm:text-xs font-600 text-primary/80 text-center leading-tight">{label}</span>
      </Link>
    ))}
  </section>
);

/* ============= TWO SHORTCUT CARDS (My Trips / My Documents) ============= */

const ShortcutRow = () => (
  <section className="grid grid-cols-2 gap-3" data-testid="home-shortcut-row">
    <ShortcutCard
      to="/travel-hub" testid="home-shortcut-trips"
      photo="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=600&q=70"
      title="My Trips" body="View and manage your upcoming and past trips" />
    <ShortcutCard
      to="/locker" testid="home-shortcut-docs"
      photo="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=600&q=70"
      title="My Documents" body="Passports, visas, insurance, tickets and more" />
  </section>
);

const ShortcutCard = ({ to, photo, title, body, testid }) => (
  <Link to={to} data-testid={testid}
    className="group relative flex items-stretch bg-card border border-border rounded-2xl overflow-hidden shadow-warm-sm hover:shadow-warm-md hover:border-accent transition-[box-shadow,border-color] min-h-[110px] tap">
    <div className="w-24 sm:w-28 shrink-0 relative">
      <img src={photo} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
    </div>
    <div className="flex-1 min-w-0 p-3 pr-8 flex flex-col justify-center">
      <div className="font-serif font-700 text-base sm:text-lg text-primary leading-tight">{title}</div>
      <div className="text-xs sm:text-sm text-muted-foreground leading-snug mt-1 line-clamp-3">{body}</div>
    </div>
    <ChevronRight className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 text-primary/60 group-hover:text-accent transition-colors" />
  </Link>
);

/* ============= UPCOMING TRIP CAROUSEL ============= */

const UpcomingTripCarousel = () => {
  const [idx, setIdx] = useState(0);
  const trackRef = useRef(null);

  const onScroll = () => {
    if (!trackRef.current) return;
    const w = trackRef.current.clientWidth;
    setIdx(Math.round(trackRef.current.scrollLeft / w));
  };
  const goTo = (i) => {
    if (!trackRef.current) return;
    trackRef.current.scrollTo({ left: i * trackRef.current.clientWidth, behavior: "smooth" });
  };

  return (
    <section data-testid="home-upcoming-carousel">
      <div ref={trackRef} onScroll={onScroll}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-none gap-3 -mx-1 px-1"
        style={{ scrollbarWidth: "none" }}>
        {UPCOMING_TRIPS.map((t) => (
          <UpcomingCard key={t.id} trip={t} />
        ))}
      </div>
      <div className="mt-2 flex items-center justify-center gap-1.5">
        {UPCOMING_TRIPS.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} data-testid={`home-carousel-dot-${i}`}
            aria-label={`Go to trip ${i + 1}`}
            className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-[hsl(var(--accent))] w-5" : "bg-primary/25"}`} />
        ))}
      </div>
    </section>
  );
};

const UpcomingCard = ({ trip }) => (
  <div className="snap-center shrink-0 w-full bg-card border border-border rounded-2xl overflow-hidden shadow-warm-sm">
    <div className="flex items-stretch min-h-[130px]">
      <div className="w-32 shrink-0 relative">
        <img src={trip.photo} alt={trip.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div className="flex-1 p-3.5 min-w-0 flex flex-col justify-center">
        <div className="font-serif font-700 text-lg text-primary leading-tight">{trip.title}</div>
        <div className="text-xs text-muted-foreground mt-1">{trip.dates}</div>
        <Link to={trip.to} data-testid={`home-upcoming-view-${trip.id}`}
          className="mt-3 inline-flex items-center justify-center gap-1 self-start px-4 py-2 rounded-full bg-emerald-500 text-white text-sm font-700 min-h-[36px] hover:bg-emerald-600 transition-colors tap">
          View Trip
        </Link>
      </div>
      <div className="w-24 shrink-0 flex items-center justify-center border-l border-border/60">
        <div className="text-center">
          <div className="font-serif font-800 text-3xl text-primary leading-none">{trip.days}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">Days</div>
          <div className="mt-2 flex items-center justify-center gap-1 text-primary">
            <Users className="w-3.5 h-3.5" strokeWidth={2} />
            <span className="font-serif font-800 text-lg leading-none">{trip.travelers}</span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Travelers</div>
        </div>
      </div>
    </div>
  </div>
);
